import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  ArrowLeft,
  Copy,
  MailCheck,
  Pencil,
  RotateCcw,
  Save,
  X,
} from "lucide-react";
import { api } from "@jourfx/convex/client";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  useToast,
} from "@jourfx/ui";

import { useDateFormat } from "../../hooks/useDateFormat";
import { STATUS_VARIANT, formatLocalDateTimeInput, parseLocalDateTimeInput } from "./shared";

type AppointmentDetailRecord = {
  _id: string;
  organizationId: string;
  organization?: { id: string; name: string } | null;
  templateId?: string;
  templateName?: string | null;
  gutachterId: string;
  gutachterName?: string | null;
  versichertenProfileId?: string;
  versicherterName?: string | null;
  versicherterEmail?: string | null;
  versicherterPhone?: string | null;
  scheduledAt: number;
  duration: number;
  timezone: string;
  status: "scheduled" | "confirmed" | "in_progress" | "completed" | "cancelled" | "no_show";
  notes?: string;
  accessCode?: string;
  expiresAt: number;
  history?: Array<{
    _id: string;
    action: string;
    timestamp: number;
    details?: Record<string, unknown> | null;
  }>;
};

type TemplateOption = {
  _id: string;
  name: string;
  duration: number;
};

type GutachterOption = {
  _id: string;
  name: string;
};

type VersicherteOption = {
  _id: string;
  firstName: string;
  lastName: string;
};

type DurationPolicy = {
  allowedDurations: number[];
  appointmentDuration: number;
};

export function AppointmentDetail() {
  const apiRef = api as any;
  const { appointmentId } = useParams({ strict: false }) as { appointmentId: string };
  const { t } = useTranslation("appointments");
  const { formatDateTime, formatRelative } = useDateFormat();
  const { toast } = useToast();

  const appointment = useQuery(apiRef.admin.appointments.get, {
    appointmentId: appointmentId as any,
  }) as AppointmentDetailRecord | null | undefined;

  const templates = useQuery(
    apiRef.admin.appointmentTemplates.list,
    appointment?.organizationId
      ? { organizationId: appointment.organizationId as any }
      : "skip"
  ) as TemplateOption[] | undefined;
  const durationPolicy = useQuery(
    apiRef.admin.appointmentTemplates.getDurationPolicyForOrganization,
    appointment?.organizationId
      ? { organizationId: appointment.organizationId as any }
      : "skip"
  ) as DurationPolicy | undefined;

  const gutachter = useQuery(
    apiRef.admin.appointments.listGutachter,
    appointment?.organizationId
      ? { organizationId: appointment.organizationId as any }
      : "skip"
  ) as GutachterOption[] | undefined;

  const versicherte = useQuery(
    apiRef.admin.versicherte.list,
    appointment?.organizationId
      ? { organizationId: appointment.organizationId as any, limit: 200 }
      : "skip"
  ) as VersicherteOption[] | undefined;

  const updateAppointment = useMutation(apiRef.admin.appointments.update);
  const updateStatus = useMutation(apiRef.admin.appointments.updateStatus);
  const resetAppointment = useMutation(apiRef.admin.appointments.reset);
  const sendReminder = useMutation(apiRef.admin.appointments.sendReminder);

  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cancellationReason, setCancellationReason] = useState("");
  const [templateDurationFallback, setTemplateDurationFallback] = useState<{
    templateName: string;
    legacyDuration: number;
    fallbackDuration: number;
  } | null>(null);
  const [formState, setFormState] = useState({
    templateId: "",
    gutachterId: "",
    versichertenProfileId: "",
    scheduledAt: "",
    duration: 30,
    timezone: "",
    notes: "",
  });

  const buildFormState = (record: AppointmentDetailRecord) => ({
    templateId: record.templateId || "",
    gutachterId: record.gutachterId || "",
    versichertenProfileId: record.versichertenProfileId || "",
    scheduledAt: formatLocalDateTimeInput(record.scheduledAt),
    duration: record.duration,
    timezone: record.timezone,
    notes: record.notes || "",
  });

  useEffect(() => {
    if (!appointment) return;
    setFormState(buildFormState(appointment));
  }, [appointment]);

  const selectedTemplate = useMemo(
    () => templates?.find((entry) => entry._id === formState.templateId) ?? null,
    [formState.templateId, templates]
  );
  const allowedDurations = useMemo(
    () =>
      durationPolicy?.allowedDurations?.length
        ? durationPolicy.allowedDurations
        : [30, 60, 90],
    [durationPolicy]
  );
  const defaultDuration = durationPolicy?.appointmentDuration ?? allowedDurations[0] ?? 30;
  const allowedDurationsKey = useMemo(
    () => allowedDurations.join(","),
    [allowedDurations]
  );
  const selectedTemplateKey = selectedTemplate
    ? `${selectedTemplate._id}:${selectedTemplate.duration}`
    : "";

  useEffect(() => {
    if (!isEditing) {
      setTemplateDurationFallback((current) => (current === null ? current : null));
      return;
    }

    if (!selectedTemplate) {
      setTemplateDurationFallback((current) => (current === null ? current : null));
      return;
    }

    if (allowedDurations.includes(selectedTemplate.duration)) {
      setTemplateDurationFallback((current) => (current === null ? current : null));
      setFormState((current) => ({
        ...current,
        duration: current.duration === selectedTemplate.duration
          ? current.duration
          : selectedTemplate.duration,
      }));
      return;
    }

    setTemplateDurationFallback((current) =>
      current?.templateName === selectedTemplate.name &&
      current.legacyDuration === selectedTemplate.duration &&
      current.fallbackDuration === defaultDuration
        ? current
        : {
            templateName: selectedTemplate.name,
            legacyDuration: selectedTemplate.duration,
            fallbackDuration: defaultDuration,
          }
    );
    setFormState((current) => ({
      ...current,
      duration: current.duration === defaultDuration ? current.duration : defaultDuration,
    }));
  }, [allowedDurationsKey, defaultDuration, isEditing, selectedTemplateKey]);

  if (appointment === undefined) {
    return <p className="text-muted-foreground">{t("detail.loading")}</p>;
  }

  if (!appointment) {
    return (
      <div className="space-y-4">
        <Link
          to="/appointments"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("detail.back")}
        </Link>
        <p>{t("detail.notFound")}</p>
      </div>
    );
  }

  const canReset =
    ["completed", "cancelled", "no_show"].includes(appointment.status) &&
    appointment.expiresAt > Date.now();

  const handleSave = async () => {
    const scheduledAt = parseLocalDateTimeInput(formState.scheduledAt);
    if (!scheduledAt) return;

    setIsSubmitting(true);
    try {
      await updateAppointment({
        appointmentId: appointment._id as any,
        patch: {
          templateId: formState.templateId || undefined,
          gutachterId: formState.gutachterId || undefined,
          versichertenProfileId: formState.versichertenProfileId || undefined,
          scheduledAt,
          duration: Number(formState.duration),
          timezone: formState.timezone || undefined,
          notes: formState.notes || undefined,
        },
      });
      setIsEditing(false);
      toast({ title: t("detail.toasts.savedTitle") });
    } catch (error) {
      toast({
        title: t("detail.toasts.errorTitle"),
        description: error instanceof Error ? error.message : t("detail.toasts.errorDescription"),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    if (appointment) {
      setFormState(buildFormState(appointment));
    }
    setIsEditing(false);
  };

  const handleStatusUpdate = async (
    status: "confirmed" | "cancelled" | "no_show"
  ) => {
    setIsSubmitting(true);
    try {
      await updateStatus({
        appointmentId: appointment._id as any,
        status,
        cancellationReason:
          status === "cancelled" ? cancellationReason || undefined : undefined,
      });
      setCancellationReason("");
      toast({ title: t("detail.toasts.statusUpdatedTitle") });
    } catch (error) {
      toast({
        title: t("detail.toasts.errorTitle"),
        description: error instanceof Error ? error.message : t("detail.toasts.errorDescription"),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = async () => {
    setIsSubmitting(true);
    try {
      await resetAppointment({ appointmentId: appointment._id as any });
      toast({ title: t("detail.toasts.resetTitle") });
    } catch (error) {
      toast({
        title: t("detail.toasts.errorTitle"),
        description: error instanceof Error ? error.message : t("detail.toasts.errorDescription"),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendReminder = async () => {
    setIsSubmitting(true);
    try {
      await sendReminder({
        appointmentId: appointment._id as any,
        channel: "email",
      });
      toast({ title: t("detail.toasts.reminderTitle") });
    } catch (error) {
      toast({
        title: t("detail.toasts.errorTitle"),
        description: error instanceof Error ? error.message : t("detail.toasts.errorDescription"),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const appointmentDateTime = formatDateTime(appointment.scheduledAt);

  return (
    <div className="space-y-6">
      <Link
        to="/appointments"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("detail.back")}
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{appointment.versicherterName}</h1>
          <p className="text-sm text-muted-foreground">{appointmentDateTime}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={STATUS_VARIANT[appointment.status]}>
            {t(`status.${appointment.status}`)}
          </Badge>
          {isEditing ? (
            <>
              <Button variant="outline" onClick={handleCancelEdit}>
                <X className="mr-2 h-4 w-4" />
                {t("detail.cancelEdit")}
              </Button>
              <Button disabled={isSubmitting} onClick={handleSave}>
                <Save className="mr-2 h-4 w-4" />
                {t("detail.save")}
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={() => setIsEditing(true)}>
              <Pencil className="mr-2 h-4 w-4" />
              {t("detail.edit")}
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-3">
        <Card className="xl:col-span-2">
          <CardHeader>
            <CardTitle>{t("detail.summaryTitle")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isEditing ? (
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="appointment-template">{t("detail.fields.template")}</Label>
                  <select
                    id="appointment-template"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={formState.templateId}
                    onChange={(event) =>
                      setFormState((current) => ({
                        ...current,
                        templateId: event.target.value,
                      }))
                    }
                  >
                    <option value="">{t("detail.selectTemplate")}</option>
                    {templates?.map((entry) => (
                      <option key={entry._id} value={entry._id}>
                        {entry.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="appointment-gutachter">{t("detail.fields.examiner")}</Label>
                  <select
                    id="appointment-gutachter"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={formState.gutachterId}
                    onChange={(event) =>
                      setFormState((current) => ({
                        ...current,
                        gutachterId: event.target.value,
                      }))
                    }
                  >
                    <option value="">{t("detail.selectExaminer")}</option>
                    {gutachter?.map((entry) => (
                      <option key={entry._id} value={entry._id}>
                        {entry.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="appointment-profile">{t("detail.fields.patient")}</Label>
                  <select
                    id="appointment-profile"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={formState.versichertenProfileId}
                    onChange={(event) =>
                      setFormState((current) => ({
                        ...current,
                        versichertenProfileId: event.target.value,
                      }))
                    }
                  >
                    <option value="">{t("detail.selectPatient")}</option>
                    {versicherte?.map((entry) => (
                      <option key={entry._id} value={entry._id}>
                        {entry.firstName} {entry.lastName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="appointment-scheduled">{t("detail.fields.scheduledAt")}</Label>
                  <Input
                    id="appointment-scheduled"
                    type="datetime-local"
                    value={formState.scheduledAt}
                    onChange={(event) =>
                      setFormState((current) => ({
                        ...current,
                        scheduledAt: event.target.value,
                      }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="appointment-duration">{t("detail.fields.duration")}</Label>
                  <select
                    id="appointment-duration"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={String(formState.duration)}
                    onChange={(event) =>
                      setFormState((current) => ({
                        ...current,
                        duration: Number(event.target.value || 0),
                      }))
                    }
                    data-testid="appointment-detail-duration-select"
                  >
                    <option value="">{t("detail.selectDuration")}</option>
                    {allowedDurations.map((entry) => (
                      <option key={entry} value={entry}>
                        {t("list.minutes", { count: entry })}
                      </option>
                    ))}
                  </select>
                  {templateDurationFallback ? (
                    <div
                      className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/5 px-3 py-2 text-sm text-warning"
                      data-testid="detail-duration-fallback"
                    >
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>
                        {t("detail.durationFallbackNotice", {
                          templateName: templateDurationFallback.templateName,
                          legacyDuration: templateDurationFallback.legacyDuration,
                          fallbackDuration: templateDurationFallback.fallbackDuration,
                        })}
                      </span>
                    </div>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="appointment-timezone">{t("detail.fields.timezone")}</Label>
                  <Input
                    id="appointment-timezone"
                    value={formState.timezone}
                    onChange={(event) =>
                      setFormState((current) => ({
                        ...current,
                        timezone: event.target.value,
                      }))
                    }
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="appointment-notes">{t("detail.fields.notes")}</Label>
                  <textarea
                    id="appointment-notes"
                    className="min-h-28 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={formState.notes}
                    onChange={(event) =>
                      setFormState((current) => ({
                        ...current,
                        notes: event.target.value,
                      }))
                    }
                  />
                </div>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <div className="text-sm text-muted-foreground">{t("detail.fields.organization")}</div>
                  <div className="font-medium">{appointment.organization?.name || "-"}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">{t("detail.fields.template")}</div>
                  <div className="font-medium">{appointment.templateName || "-"}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">{t("detail.fields.patient")}</div>
                  <div className="font-medium">{appointment.versicherterName || "-"}</div>
                  <div className="text-sm text-muted-foreground">
                    {appointment.versicherterEmail || appointment.versicherterPhone || ""}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">{t("detail.fields.examiner")}</div>
                  <div className="font-medium">{appointment.gutachterName || "-"}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">{t("detail.fields.scheduledAt")}</div>
                  <div className="font-medium">{appointmentDateTime}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">{t("detail.fields.duration")}</div>
                  <div className="font-medium">{t("list.minutes", { count: appointment.duration })}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">{t("detail.fields.timezone")}</div>
                  <div className="font-medium">{appointment.timezone}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">{t("detail.fields.notes")}</div>
                  <div className="whitespace-pre-wrap font-medium">
                    {appointment.notes || t("detail.noNotes")}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t("detail.credentialsTitle")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg border p-3">
                <div className="text-sm text-muted-foreground">{t("credentials.accessCode")}</div>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <span className="font-mono font-semibold">{appointment.accessCode || "-"}</span>
                  {appointment.accessCode ? (
                    <button
                      type="button"
                      onClick={() => navigator.clipboard.writeText(appointment.accessCode!)}
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  ) : null}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t("detail.actionsTitle")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button className="w-full justify-start" variant="outline" onClick={handleSendReminder}>
                <MailCheck className="mr-2 h-4 w-4" />
                {t("detail.sendReminder")}
              </Button>
              {appointment.status === "scheduled" ? (
                <Button className="w-full" onClick={() => void handleStatusUpdate("confirmed")}>
                  {t("detail.confirm")}
                </Button>
              ) : null}
              {["scheduled", "confirmed"].includes(appointment.status) ? (
                <>
                  <Input
                    value={cancellationReason}
                    onChange={(event) => setCancellationReason(event.target.value)}
                    placeholder={t("detail.cancellationReason")}
                  />
                  <Button
                    className="w-full"
                    variant="destructive"
                    onClick={() => void handleStatusUpdate("cancelled")}
                  >
                    {t("detail.cancel")}
                  </Button>
                </>
              ) : null}
              {appointment.status === "confirmed" ? (
                <Button
                  className="w-full"
                  variant="outline"
                  onClick={() => void handleStatusUpdate("no_show")}
                >
                  {t("detail.noShow")}
                </Button>
              ) : null}
              {canReset ? (
                <Button className="w-full" variant="outline" onClick={handleReset}>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  {t("detail.reset")}
                </Button>
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t("detail.historyTitle")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {appointment.history?.length ? (
                appointment.history.map((entry) => (
                  <div key={entry._id} className="rounded-lg border p-3">
                    <div className="font-medium">{entry.action}</div>
                    <div className="text-sm text-muted-foreground">
                      {formatDateTime(entry.timestamp)} ({formatRelative(entry.timestamp)})
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">{t("detail.noHistory")}</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
